import type { IRequiredActionIssue } from '@/composables/requiredAction/requiredAction.types';
import { NOTE_TEXT_MAX_LENGTH } from 'common/constants/note.constants';
import { SUPPORT_MEASURE_DESCRIPTION_MAX_LENGTH } from 'common/constants/support.measure.constants';
import { EQuickEntryKind } from './quickEntry.enums';
import type { IQuickEntryDraft } from './quickEntry.utils';

export const QuickEntryRequiredAction = {
  PerformanceAssessment: 'performanceAssessment',
  SchoolSubject: 'schoolSubject',
  Students: 'students',
  Text: 'text',
} as const;

export type TQuickEntryRequiredAction = (typeof QuickEntryRequiredAction)[keyof typeof QuickEntryRequiredAction];

export type TQuickEntryValidationIssue = IRequiredActionIssue<TQuickEntryRequiredAction>;

export interface IQuickEntryValidationContext {
  draft: Pick<IQuickEntryDraft, 'kind' | 'text'>;
  hasResolvedPerformanceAssessment: boolean;
  hasResolvedSchoolSubject: boolean;
  resolvedStudentCount: number;
}

type TQuickEntryRequirement = (context: IQuickEntryValidationContext) => TQuickEntryValidationIssue | null;

const requiredIssue = (
  action: TQuickEntryRequiredAction,
  code: string,
  messageKey: string,
): TQuickEntryValidationIssue => ({ action, code, messageKey });

const requireSchoolSubject: TQuickEntryRequirement = (context) =>
  context.hasResolvedSchoolSubject
    ? null
    : requiredIssue(
        QuickEntryRequiredAction.SchoolSubject,
        'schoolSubject.required',
        'quickEntry.requiredAction.schoolSubject',
      );

const requirePerformanceAssessment: TQuickEntryRequirement = (context) =>
  context.hasResolvedPerformanceAssessment
    ? null
    : requiredIssue(
        QuickEntryRequiredAction.PerformanceAssessment,
        'performanceAssessment.required',
        'quickEntry.requiredAction.performanceAssessment',
      );

const requireStudents: TQuickEntryRequirement = (context) =>
  context.resolvedStudentCount > 0
    ? null
    : requiredIssue(QuickEntryRequiredAction.Students, 'students.required', 'quickEntry.requiredAction.students');

const requireText: TQuickEntryRequirement = (context) =>
  context.draft.text.trim().length > 0
    ? null
    : requiredIssue(QuickEntryRequiredAction.Text, 'text.required', 'quickEntry.requiredAction.text');

const validateTextLength =
  (maxLength: number): TQuickEntryRequirement =>
  (context) =>
    context.draft.text.trim().length <= maxLength
      ? null
      : requiredIssue(QuickEntryRequiredAction.Text, 'text.tooLong', 'quickEntry.requiredAction.textTooLong');

const REQUIREMENTS_BY_KIND: Record<EQuickEntryKind, readonly TQuickEntryRequirement[]> = {
  [EQuickEntryKind.Note]: [requireText, validateTextLength(NOTE_TEXT_MAX_LENGTH)],
  [EQuickEntryKind.PerformanceAssessment]: [
    requireSchoolSubject,
    requirePerformanceAssessment,
    requireStudents,
    validateTextLength(NOTE_TEXT_MAX_LENGTH),
  ],
  [EQuickEntryKind.SupportMeasure]: [
    requireSchoolSubject,
    requireStudents,
    validateTextLength(SUPPORT_MEASURE_DESCRIPTION_MAX_LENGTH),
  ],
};

export const getQuickEntryValidationIssues = (context: IQuickEntryValidationContext): TQuickEntryValidationIssue[] =>
  REQUIREMENTS_BY_KIND[context.draft.kind]
    .map((requirement) => requirement(context))
    .filter((issue): issue is TQuickEntryValidationIssue => issue !== null);
